export interface RetryPolicy {
  maxRetries: number;
  retryDelay: number;
  backoffMultiplier?: number;
  maxDelay?: number;
  retryOnFailure?: boolean;
}

export class SensorInjection {
    id: string;
    sensorSettingId: string;
    type: string;
    settingId: string;
    commandType: string;
    retryPolicy: RetryPolicy;
    requiresAck: boolean;
    active: boolean;
    createdAt: Date;
    updatedAt: Date;

    private spanishNames?: Record<string, string> = {
        id: "identificador",
        sensorSettingId: "ID de configuración de sensor",
        type: "tipo",
        settingId: "ID de configuración",
        commandType: "tipo de comando",
        retryPolicy: "política de reintentos",
        requiresAck: "requiere confirmación",
        active: "activo",
        createdAt: "creado en",
        updatedAt: "actualizado en",
    };

    constructor(data?: Partial<SensorInjection>) {
        this.id = data?.id ?? "";
        this.sensorSettingId = data?.sensorSettingId ?? "";
        this.type = data?.type ?? "";
        this.settingId = data?.settingId ?? "";
        this.commandType = data?.commandType ?? "";
        this.retryPolicy = data?.retryPolicy ?? {
            maxRetries: 3,
            retryDelay: 1000,
            backoffMultiplier: 2,
            maxDelay: 30000,
            retryOnFailure: true
        };
        this.requiresAck = data?.requiresAck ?? false;
        this.active = data?.active ?? false;
        this.createdAt = data?.createdAt ? new Date(data.createdAt) : new Date();
        this.updatedAt = data?.updatedAt ? new Date(data.updatedAt) : new Date();
    }

    getSpanishName(propertyName: string): string {
        if (this.spanishNames && this.spanishNames[propertyName]) {
            return this.spanishNames[propertyName];
        }
        return propertyName;
    }

    toPlainObject(): Record<string, any> {
        return {
            id: this.id,
            sensorSettingId: this.sensorSettingId,
            type: this.type,
            settingId: this.settingId,
            commandType: this.commandType,
            retryPolicy: this.retryPolicy,
            requiresAck: this.requiresAck,
            active: this.active,
            createdAt: this.createdAt,
            updatedAt: this.updatedAt,
        };
    }

    // Métodos de utilidad específicos para SensorInjection
    getRetryPolicyDescription(): string {
        const { maxRetries, retryDelay, backoffMultiplier } = this.retryPolicy;
        const delayInSeconds = retryDelay / 1000;
        
        if (backoffMultiplier && backoffMultiplier > 1) {
            return `${maxRetries} reintentos con ${delayInSeconds}s de delay inicial (backoff x${backoffMultiplier})`;
        }
        return `${maxRetries} reintentos con ${delayInSeconds}s de delay`;
    }

    isReliable(): boolean {
        return this.requiresAck && this.retryPolicy.maxRetries > 0;
    }

    getCommandTypeDescription(): string {
        const commandTypes: Record<string, string> = {
            'SET_VALUE': 'Establecer valor',
            'GET_STATUS': 'Obtener estado',
            'CALIBRATE': 'Calibrar',
            'RESET': 'Reiniciar',
            'CONFIG': 'Configurar',
            'START': 'Iniciar',
            'STOP': 'Detener',
            'CUSTOM': 'Personalizado'
        };
        
        return commandTypes[this.commandType] || this.commandType;
    }

    getMaxRetryDelay(): number {
        const { retryDelay, backoffMultiplier, maxDelay } = this.retryPolicy;
        
        if (!backoffMultiplier || backoffMultiplier <= 1) {
            return retryDelay;
        }
        
        let currentDelay = retryDelay;
        for (let i = 1; i < this.retryPolicy.maxRetries; i++) {
            currentDelay *= backoffMultiplier;
            if (maxDelay && currentDelay > maxDelay) {
                return maxDelay;
            }
        }
        
        return currentDelay;
    }
}
